/**************************************************************************************************/
/*   File: /src/app/stores/actions/files/regulation.files.actions.ts                              */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 15th 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Thu May 09 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { Action } from '@ngrx/store';

import * as fromShared from '../../../shared';

/**
 * The file action types enum
 */
export enum OtherFileActionTypes {
  /**
   * The file added action's identifier
   */
  OTHER_FILE_ADDED = '[Editing][File] Other File added',
  /**
   * The file removed action's identifier
   */
  OTHER_FILE_REMOVED = '[Editing][File] Other File removed',
  /**
   * The file removed success action's identifier
   */
  OTHER_FILE_REMOVED_SUCCESS = '[Editing][File] Other File removed success',
  /**
   * The file removed fail action's identifier
   */
  OTHER_FILE_REMOVED_FAIL = '[Editing][File] Other File removed fail',
  /**
   * The file clead action's identifier
   */
  OTHER_FILE_CLEAR = '[Editing][File] Other File clear',
  /**
   * The file upload action's identifier
   */
  OTHER_FILE_UPLOAD = '[Editing][File] Other File upload',
  /**
   * The file upload success action's identifier
   */
  OTHER_FILE_UPLOAD_SUCCESS = '[Editing][File] Other File upload success',
  /**
   * The file upload failure action's identifier
   */
  OTHER_FILE_UPLOAD_FAILURE = '[Editing][File] Other File upload fail'
}
/**
 * Action dispatched when a file is ready to be uploaded 
 * and added to the collection of file required for upload
 */
export class OtherFileAddAction implements Action {
  /**
   * The type
   */
  readonly type = OtherFileActionTypes.OTHER_FILE_ADDED;
  /**
   * The constructor
   * @param payload The payload of files and fileType
   */
  constructor(public payload: { files: Array<fromShared.models.FileModel> }) { }
}
/**
 * Action dispatched when a file is removed
 * from the collection of file required for upload
 */
export class OtherFileRemovedAction implements Action {
  /**
   * The type
   */
  readonly type = OtherFileActionTypes.OTHER_FILE_REMOVED;
  /**
   * The constructor
   * @param payload The payload is the id of element to remove and fileType
   */
  constructor(public payload: { id: string, isUploaded: boolean }) { }
}
/**
 * Action dispatched when a file is removed also from remote
 */
export class OtherFileRemovedSuccessAction implements Action {
  /**
   * The type
   */
  readonly type = OtherFileActionTypes.OTHER_FILE_REMOVED_SUCCESS;
  /**
   * The constructor
   * @param payload The payload is the id of element to remove and fileType
   */
  constructor(public payload: { id: string }) { }
}
/**
 * Action dispatched when a file cannot be removed from remote
 */
export class OtherFileRemovedFailAction implements Action {
  /**
   * The type
   */
  readonly type = OtherFileActionTypes.OTHER_FILE_REMOVED_FAIL;
  /**
   * The constructor
   * @param payload The payload is the message
   */
  constructor(public payload: { message: fromShared.models.Message }) { }
}
/**
 * Action dispatched when the collection of file required for upload
 * is resetted
 */
export class OtherFileClearAction implements Action {
  /**
   * The type
   */
  readonly type = OtherFileActionTypes.OTHER_FILE_CLEAR;
  /**
   * The constructor
   * @param payload The payload is the context
   */
  constructor(public payload: { files: fromShared.models.FileModel[] }) { }
}
/**
 * Action dispatched when user wants to upload all the files prepared in the collection
 * with other actions previously described
 */
export class OtherFileUploadAction implements Action {
  /**
   * The type
   */
  readonly type = OtherFileActionTypes.OTHER_FILE_UPLOAD;
  /**
   * The constructor
   * @param payload The payload is the single file and the fileType
   */
  constructor(public payload: { file: fromShared.models.FileModel }) { }
}
/**
 * Action dispatched when file are correctly uploaded to the middleware
 */
export class OtherFileUploadActionSuccess implements Action {
  /**
   * The type
   */
  readonly type = OtherFileActionTypes.OTHER_FILE_UPLOAD_SUCCESS;
  /**
   * The constructor
   * @param payload The payload is the single file and the  fileType: fromShared.enums.FileType
   */
  constructor(public payload: {
    file: fromShared.models.FileModel,
    documentId: fromShared.models.LocalDocumentId
  }) { }
}
/**
 * Action dispatched when file upload throws an exception
 */
export class OtherFileUploadActionFail implements Action {
  /**
   * The type
   */
  readonly type = OtherFileActionTypes.OTHER_FILE_UPLOAD_FAILURE;

  /**
   * The constructor
   * @param payload The error to display and the context
   */
  constructor(public payload: { file: fromShared.models.FileModel, message: fromShared.models.Message }) { }
}

/**
 * The file action type
 */
export type OtherFileActions =
  OtherFileAddAction |
  OtherFileRemovedAction |
  OtherFileRemovedSuccessAction |
  OtherFileRemovedFailAction |
  OtherFileClearAction |
  OtherFileUploadAction |
  OtherFileUploadActionSuccess |
  OtherFileUploadActionFail;
